import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom hook to detect when an element is in the viewport
 * @param {object} options
 * @param {number} options.threshold - Percentage of element visible to trigger (0-1)
 * @param {boolean} options.triggerOnce - Only trigger once when element comes into view
 * @returns {{ ref: React.RefObject, inView: boolean }}
 */
const useInView = ({ threshold = 0, triggerOnce = false } = {}) => {
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  const observerRef = useRef(null);

  const setRef = useCallback(
    (node) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (node) {
        observerRef.current = new IntersectionObserver(
          ([entry]) => {
            setInView(entry.isIntersecting);
            if (entry.isIntersecting && triggerOnce) {
              observerRef.current.disconnect();
            }
          },
          { threshold },
        );
        observerRef.current.observe(node);
      }

      ref.current = node;
    },
    [threshold, triggerOnce],
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { ref: setRef, inView };
};

export default useInView;
